import { useState } from 'react';
import { ChevronDown, Mail, Phone } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function FAQ() {
  const faqs = [
    {
      question: 'What areas do you deliver to?',
      answer: 'We deliver to all ZIP codes in the White Mountain Region of New Hampshire. This includes towns like Franconia, Lincoln, Conway, Gorham, Berlin, and surrounding areas.'
    },
    {
      question: 'Do I need to be home for delivery?',
      answer: 'No, you don\'t need to be home. All our boxes are insulated and packed to stay fresh for several hours. We\'ll leave your box in a safe location that you specify.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes! There are no long-term commitments. You can skip deliveries, pause your subscription, or cancel completely at any time through your dashboard.'
    },
    {
      question: 'Where does the food come from?',
      answer: 'All our products come from small, local farms in New Hampshire. We partner directly with farmers in the White Mountain Region who share our commitment to quality and sustainability.'
    },
    {
      question: 'Are the farms organic?',
      answer: 'Some of our partner farms are certified organic, while others use sustainable, pesticide-free practices without formal certification. We clearly label organic products in our shop.'
    },
    {
      question: 'How do I change my delivery schedule?',
      answer: 'You can update your subscription frequency (weekly, bi-weekly, or monthly) anytime through your account dashboard. Changes take effect on your next scheduled delivery.'
    },
    {
      question: 'What if I\'m not satisfied with my order?',
      answer: 'Your satisfaction is our priority. If any item doesn\'t meet your expectations, contact us within 24 hours of delivery and we\'ll make it right with a refund or replacement.'
    },
    {
      question: 'Can I customize what\'s in my box?',
      answer: 'While our curated boxes are designed by our farmers for optimal freshness and variety, you can add individual items from our add-ons collection to complement your order.'
    },
    {
      question: 'How is the food packaged?',
      answer: 'We use eco-friendly, insulated packaging to keep your food fresh during delivery. Our packaging is designed to maintain optimal temperature and can be recycled or composted.'
    },
    {
      question: 'What are your delivery days?',
      answer: 'We deliver on Tuesdays, Thursdays, and Saturdays. You can select your preferred delivery day during checkout or change it anytime in your account settings.'
    }
  ];

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="faq-page">
      {/* Header */}
      <section className="py-16 bg-dark">
        <div className="container-custom">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-cream mb-4" data-testid="faq-title">
            Frequently Asked Questions
          </h1>
          <p className="font-manrope text-cream/70 text-lg max-w-xl">
            Got questions? We've got answers. If you can't find what you're looking for, reach out to us.
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="section-padding">
        <div className="container-custom max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-2xl border border-forest/10 px-6 overflow-hidden"
              >
                <AccordionTrigger className="font-manrope font-bold text-dark text-left py-6 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-manrope text-dark/70 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-3xl text-center">
          <h2 className="font-playfair text-3xl font-bold text-dark mb-4">
            Still Have Questions?
          </h2>
          <p className="font-manrope text-dark/70 mb-8">
            Our team is here to help. Reach out and we'll get back to you as soon as possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="mailto:farmboxadmin@gmail.com"
              className="flex items-center justify-center gap-3 px-8 py-4 bg-forest text-cream rounded-full font-manrope font-bold hover:bg-forest-dark transition-colors"
              data-testid="faq-email"
            >
              <Mail className="w-5 h-5" />
              farmboxadmin@gmail.com
            </a>
            <a
              href="tel:+12074133262"
              className="flex items-center justify-center gap-3 px-8 py-4 border-2 border-forest text-forest rounded-full font-manrope font-bold hover:bg-forest hover:text-cream transition-colors"
              data-testid="faq-phone"
            >
              <Phone className="w-5 h-5" />
              (207) 413-3262
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
